import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api/client";
import { useClients, useCommunes } from "./Clients";

export type Chantier = {
  id: number;
  clientId: number;
  clientNom: string;
  titre: string;
  communeId: number | null;
  communeNom: string;
  adresse: string;
  prestations: number[];
  dateDebut: string | null;
  dateFin: string | null;
  statut: "prospect" | "en_cours" | "termine";
  budget: number | null;
  description: string;
  notesInternes: string;
  /** Réalisation publiée depuis ce chantier, si elle existe encore. */
  realisationId: number | null;
  createdAt: string;
};

export const LIBELLES_STATUT_CHANTIER: Record<Chantier["statut"], string> = {
  prospect: "Prospect",
  en_cours: "En cours",
  termine: "Terminé",
};

export const COULEURS_STATUT_CHANTIER: Record<Chantier["statut"], "default" | "info" | "success"> =
  {
    prospect: "default",
    en_cours: "info",
    termine: "success",
  };

export function useChantiers(statut?: string) {
  const requete = statut ? `?statut=${statut}` : "";
  return useQuery({
    queryKey: ["chantiers", statut ?? "tous"],
    queryFn: () => api.get<{ chantiers: Chantier[] }>(`/api/chantiers${requete}`),
  });
}

export function montantLisible(budget: number | null): string {
  if (budget === null) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(budget);
}

export function Chantiers() {
  const client = useQueryClient();
  const naviguer = useNavigate();
  const [filtre, setFiltre] = useState("");
  const chantiers = useChantiers(filtre || undefined);
  const clients = useClients();
  const communes = useCommunes();
  const [ouvert, setOuvert] = useState(false);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const creer = useMutation({
    mutationFn: (champs: Record<string, unknown>) =>
      api.post<{ chantier: Chantier }>("/api/chantiers", champs),
    onSuccess: (resultat) => {
      setErreurs({});
      setOuvert(false);
      client.invalidateQueries({ queryKey: ["chantiers"] });
      naviguer(`/chantiers/${resultat.chantier.id}`);
    },
    onError: (erreur: Error & { erreurs?: Record<string, string> }) => {
      setErreurs(erreur.erreurs ?? { _global: erreur.message });
    },
  });

  function soumettre(evenement: React.FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    const donnees = new FormData(evenement.currentTarget);
    const communeId = donnees.get("communeId");
    const budget = donnees.get("budget");

    creer.mutate({
      clientId: Number(donnees.get("clientId")),
      titre: donnees.get("titre"),
      communeId: communeId === "" ? null : Number(communeId),
      adresse: donnees.get("adresse"),
      dateDebut: donnees.get("dateDebut"),
      dateFin: donnees.get("dateFin"),
      statut: donnees.get("statut"),
      budget: budget === "" ? null : Number(budget),
      description: donnees.get("description"),
    });
  }

  const liste = chantiers.data?.chantiers ?? [];
  const aucunClient = (clients.data?.clients.length ?? 0) === 0;

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
        spacing={2}
      >
        <Typography variant="h5" component="h1">
          Chantiers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={aucunClient}
          onClick={() => setOuvert(true)}
        >
          Nouveau chantier
        </Button>
      </Stack>

      {aucunClient && (
        <Alert severity="info">
          Un chantier est toujours rattaché à un client. Créez d'abord un client, ou convertissez
          une demande de devis en client depuis sa fiche.
        </Alert>
      )}

      <TextField
        select
        size="small"
        label="Statut"
        value={filtre}
        onChange={(evenement) => setFiltre(evenement.target.value)}
        sx={{ maxWidth: 220 }}
      >
        <MenuItem value="">Tous</MenuItem>
        {(Object.keys(LIBELLES_STATUT_CHANTIER) as Chantier["statut"][]).map((statut) => (
          <MenuItem key={statut} value={statut}>
            {LIBELLES_STATUT_CHANTIER[statut]}
          </MenuItem>
        ))}
      </TextField>

      {chantiers.isPending ? (
        <LinearProgress />
      ) : liste.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Aucun chantier. Un chantier terminé peut être publié en réalisation : c'est ce qui
            alimente les pages de commune et le référencement local.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Chantier</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Commune</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell>Publié</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {liste.map((chantier) => (
                <TableRow
                  key={chantier.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => naviguer(`/chantiers/${chantier.id}`)}
                >
                  <TableCell>{chantier.titre}</TableCell>
                  <TableCell>{chantier.clientNom}</TableCell>
                  <TableCell>{chantier.communeNom || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={LIBELLES_STATUT_CHANTIER[chantier.statut]}
                      color={COULEURS_STATUT_CHANTIER[chantier.statut]}
                    />
                  </TableCell>
                  <TableCell align="right">{montantLisible(chantier.budget)}</TableCell>
                  <TableCell>
                    {chantier.realisationId === null ? (
                      "—"
                    ) : (
                      <Chip size="small" label="En ligne" color="secondary" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={ouvert} onClose={() => setOuvert(false)} fullWidth maxWidth="sm">
        <form onSubmit={soumettre}>
          <DialogTitle>Nouveau chantier</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {erreurs._global && <Alert severity="error">{erreurs._global}</Alert>}
              <TextField
                name="clientId"
                label="Client"
                select
                required
                fullWidth
                defaultValue=""
                error={Boolean(erreurs.clientId)}
                helperText={erreurs.clientId}
              >
                {clients.data?.clients.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nom}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="titre"
                label="Titre"
                required
                fullWidth
                error={Boolean(erreurs.titre)}
                helperText={erreurs.titre}
              />
              <TextField name="communeId" label="Commune" select defaultValue="" fullWidth>
                <MenuItem value="">Non précisée</MenuItem>
                {communes.data?.communes.map((commune) => (
                  <MenuItem key={commune.id} value={commune.id}>
                    {commune.nom}
                  </MenuItem>
                ))}
              </TextField>
              <TextField name="adresse" label="Adresse" fullWidth />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  name="dateDebut"
                  label="Début"
                  type="date"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  name="dateFin"
                  label="Fin"
                  type="date"
                  fullWidth
                  error={Boolean(erreurs.dateFin)}
                  helperText={erreurs.dateFin}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField name="statut" label="Statut" select defaultValue="prospect" fullWidth>
                  {(Object.keys(LIBELLES_STATUT_CHANTIER) as Chantier["statut"][]).map((statut) => (
                    <MenuItem key={statut} value={statut}>
                      {LIBELLES_STATUT_CHANTIER[statut]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField name="budget" label="Budget (€)" type="number" fullWidth />
              </Stack>
              <TextField name="description" label="Description" multiline rows={3} fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOuvert(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={creer.isPending}>
              {creer.isPending ? "Enregistrement…" : "Créer"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Stack>
  );
}
